// @flow

import { PureComponent } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import includes from 'lodash/includes';

import type {
  User,
  Shift,
  Persona,
  Note,
  AppState,
  Screen,
  ButtonTheme,
  NothingToDoScreenProps,
  InitializeActionCreator,
  LogInActionCreator,
  SignOutActionCreator,
  ClockInActionCreator,
  StartBreakActionCreator,
  StartAutomatedBreakActionCreator,
  EndBreakActionCreator,
  ClockOutActionCreator,
  AdHocClockInActionCreator,
  UpdateShiftActionCreator,
  SetHideGreetingActionCreator
} from './common/typings';
import type { State as GlobalState } from './common/rootReducer';
import AppLoader from './common/components/AppLoader';
import DisableDoubleTapZoom from './common/components/DisableDoubleTapZoom';
import MainScreen from './screens/MainScreen';
import PinPadScreen from './screens/PinPadScreen';
import UserPanel from './common/components/UserPanel/UserPanel';
import AppStatusScreen from './common/components/AppStatusScreen';
import CurrentTimePresenter from './common/components/CurrentTimePresenter/CurrentTimePresenter';
import SignOutButton from './common/components/SignOutButton';
import CustomToasts from './common/components/CustomToasts/CustomToasts';
import FixedLogo from './common/components/ui/FixedLogo';
import WarningFullScreenBar from './common/components/CustomToasts/WarningFullScreenBar';
import { initialize } from './core/actions';
import { logIn, signOut } from './authentication/actions';
import {
  clockIn,
  startBreak,
  startAutomatedBreak,
  endBreak,
  clockOut,
  adHocClockIn,
  updateShift,
  setHideGreeting
} from './timeClock/actions';
import {
  APP_STATE_CHOOSE_SHIFT,
  APP_STATE_INITIALIZING,
  APP_STATE_NOTHING_TO_DO,
  APP_STATE_CLOCKED_OUT,
  BUTTON_THEME_BRAND,
  BUTTON_HEIGHT,
  SMALL_DEVICE_WIDTH,
  SUPPORTED_BUTTON_APP_STATES
} from './constants';
import logoBg from './assets/svg/Misc/background_logo.svg';

const GlobalContainer = styled.div`
  overflow-x: hidden;
`;

const BgImage = styled.img`
  z-index: 3;
  position: fixed;
  top: 0;
  left: 0;
  min-height: 100%;
  pointer-events: none;
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-left: 10%;

  z-index: 2;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  pointer-events: none;
  background-color: transparent;

  @media (max-width: ${SMALL_DEVICE_WIDTH}px) {
    display: none;
  }
`;

const TimeArea = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  align-items: center;
  justify-content: center;
`;

const RightColumn = styled.div`
  display: flex;
  justify-content: center;

  z-index: 4;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: transparent;
`;

const ButtonContainer = styled.div`
  padding-top: 20px;
  height: ${BUTTON_HEIGHT};
`;

type Props = {
  clockInEarlyMinutes: number,
  shiftBreakType: boolean,
  user: User | null,
  notes: Array<Note> | null,
  isInitialized: boolean,
  isUserLoggedIn: boolean,
  isIntegrationKiosk: boolean,
  usTimeFormat: boolean,
  loadingUserShifts: boolean,
  shifts: Array<Shift>,
  shift: Shift | null,
  adHocShift: Shift | null,
  personas?: Array<Persona>,
  appState: AppState,
  screen: Screen | null,
  screenProps: NothingToDoScreenProps,
  kioskSessionId: number | null,
  initialize: InitializeActionCreator,
  logIn: LogInActionCreator,
  signOut: SignOutActionCreator,
  clockIn: ClockInActionCreator,
  startBreak: StartBreakActionCreator,
  startAutomatedBreak: StartAutomatedBreakActionCreator,
  endBreak: EndBreakActionCreator,
  clockOut: ClockOutActionCreator,
  adHocClockIn: AdHocClockInActionCreator,
  updateShift: UpdateShiftActionCreator,
  hideGreeting: boolean,
  setHideGreeting: SetHideGreetingActionCreator,
  automatedBreaksIsEnabled: boolean
};

type State = {
  appStatusVisible: boolean,
  buttonTheme: ButtonTheme,
  width: number,
  height: number,
  pinPadInitialMount: boolean,
  pinPadVisible: boolean,
  userPanelVisible: boolean
};

export class MainComponent extends PureComponent<Props, State> {
  componentDidMount() {
    this.updateWindowDimensions();
    window.addEventListener('resize', this.updateWindowDimensions);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateWindowDimensions);
    clearTimeout(this.pinPadTimeoutId);
    clearTimeout(this.userPanelTimeoutId);
    clearTimeout(this.signOutTimeoutId);
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillMount() {
    const { isInitialized, initialize } = this.props;
    if (!isInitialized) {
      initialize();
    }
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps({ appState, isUserLoggedIn, screen }: Props) {
    const newState = {};
    let shouldUpdateState = false;

    if (appState !== this.props.appState) {
      shouldUpdateState = true;
      newState.buttonTheme = this.getButtonTheme(appState);
    }

    if (isUserLoggedIn !== this.props.isUserLoggedIn) {
      shouldUpdateState = true;
      newState.pinPadInitialMount = false;
      if (isUserLoggedIn) {
        this.props.setHideGreeting(false);
      }

      this.coordinateScreenAnimations(isUserLoggedIn);
    }

    if (screen !== this.props.screen) {
      shouldUpdateState = true;
      const appStatusVisible = Boolean(
        screen && screen !== APP_STATE_CHOOSE_SHIFT && screen !== APP_STATE_INITIALIZING
      );
      const isPinShownAfterPerformingAction = !appStatusVisible && this.state.appStatusVisible;

      newState.appStatusVisible = appStatusVisible;
      if (isPinShownAfterPerformingAction) {
        this.props.setHideGreeting(true);
      }
    }

    if (shouldUpdateState) {
      this.setState(newState);
    }
  }

  state = {
    pinPadVisible: true,
    userPanelVisible: false,
    appStatusVisible: false,
    shouldDisplay: true,
    buttonTheme: BUTTON_THEME_BRAND,
    width: 0,
    height: 0,
    pinPadInitialMount: true
  };

  pinPadTimeoutId: TimeoutID;
  userPanelTimeoutId: TimeoutID;
  signOutTimeoutId: TimeoutID;

  coordinateScreenAnimations(userLoggedIn: boolean) {
    if (userLoggedIn) {
      // just logged in
      this.setState({ pinPadVisible: false });

      setTimeout(() => {
        this.setState({ userPanelVisible: true });
      }, 300);
    } else {
      // just logged out
      this.setState({ userPanelVisible: false });

      setTimeout(() => {
        this.setState({ pinPadVisible: true });
      }, 300);
    }
  }

  updateWindowDimensions = () => {
    this.setState({ width: window.innerWidth, height: window.innerHeight });
  };

  back = () => {
    this.props.signOut();
  };

  getButtonTheme(appState: AppState): ButtonTheme {
    if (appState === APP_STATE_NOTHING_TO_DO) {
      return APP_STATE_CLOCKED_OUT;
    }
    return includes(SUPPORTED_BUTTON_APP_STATES, appState)
      ? ((appState: any): ButtonTheme)
      : BUTTON_THEME_BRAND;
  }

  render() {
    const {
      isInitialized,
      isUserLoggedIn,
      isIntegrationKiosk,
      usTimeFormat,
      clockInEarlyMinutes,
      shiftBreakType,
      user,
      loadingUserShifts,
      shifts,
      shift,
      adHocShift,
      personas,
      notes,
      logIn,
      signOut,
      clockIn,
      startBreak,
      startAutomatedBreak,
      endBreak,
      clockOut,
      adHocClockIn,
      appState,
      screen,
      screenProps,
      updateShift,
      kioskSessionId
    } = this.props;

    return (
      <GlobalContainer>
        <FixedLogo
          isUserLoggedIn={isUserLoggedIn}
          screen={screen}
          screenWidth={this.state.width}
          kioskSessionId={kioskSessionId}
        />
        <CustomToasts deviceWidth={this.state.width} />
        <MainScreen appState={appState}>
          <DisableDoubleTapZoom />
          <BgImage src={logoBg} />
          {!isInitialized && <AppLoader />}
        </MainScreen>
        <WarningFullScreenBar />

        {isInitialized && (
          <LeftColumn>
            <TimeArea>
              <CurrentTimePresenter />
              <ButtonContainer>
                {isUserLoggedIn && (
                  <SignOutButton buttonTheme={this.state.buttonTheme} signOut={signOut} />
                )}
              </ButtonContainer>
            </TimeArea>
          </LeftColumn>
        )}

        {isInitialized && (
          <RightColumn appState={appState}>
            <PinPadScreen
              shouldShow={this.state.pinPadVisible}
              logIn={logIn}
              pinPadInitialMount={this.state.pinPadInitialMount}
              deviceWidth={this.state.width}
              deviceHeight={this.state.height}
            />
          </RightColumn>
        )}

        {isInitialized && (
          <UserPanel
            user={user}
            usTimeFormat={usTimeFormat}
            clockInEarlyMinutes={clockInEarlyMinutes}
            shiftBreakType={shiftBreakType}
            loadingUserShifts={loadingUserShifts}
            shifts={shifts}
            shift={shift}
            adHocShift={adHocShift}
            personas={personas}
            notes={notes}
            signOut={signOut}
            clockIn={clockIn}
            startBreak={startBreak}
            startAutomatedBreak={startAutomatedBreak}
            endBreak={endBreak}
            clockOut={clockOut}
            adHocClockIn={adHocClockIn}
            appState={appState}
            appStatusVisible={this.state.appStatusVisible}
            hideGreeting={this.props.hideGreeting}
            screen={screen}
            deviceWidth={this.state.width}
            deviceHeight={this.state.height}
            shouldBringToFront={this.state.userPanelVisible}
            updateShift={updateShift}
            isIntegrationKiosk={isIntegrationKiosk}
            automatedBreaksIsEnabled={this.props.automatedBreaksIsEnabled}
          />
        )}

        {isInitialized && (
          <AppStatusScreen
            usTimeFormat={usTimeFormat}
            appState={appState}
            screenProps={screenProps}
            back={this.back}
            deviceWidth={this.state.width}
            appStatusVisible={this.state.appStatusVisible}
            clockInEarlyMinutes={clockInEarlyMinutes}
            isIntegrationKiosk={isIntegrationKiosk}
          />
        )}
      </GlobalContainer>
    );
  }
}

MainComponent.propTypes = {
  isInitialized: PropTypes.bool.isRequired,
  initialize: PropTypes.func.isRequired,
  isUserLoggedIn: PropTypes.bool.isRequired,
  isIntegrationKiosk: PropTypes.bool.isRequired,
  usTimeFormat: PropTypes.bool.isRequired,
  clockInEarlyMinutes: PropTypes.number.isRequired,
  shiftBreakType: PropTypes.bool,
  user: PropTypes.object,
  loadingUserShifts: PropTypes.bool,
  shifts: PropTypes.array,
  shift: PropTypes.object,
  adHocShift: PropTypes.object,
  personas: PropTypes.array,
  notes: PropTypes.array,
  appState: PropTypes.string.isRequired,
  screen: PropTypes.string,
  screenProps: PropTypes.object,
  logIn: PropTypes.func.isRequired,
  signOut: PropTypes.func.isRequired,
  clockIn: PropTypes.func.isRequired,
  startBreak: PropTypes.func.isRequired,
  endBreak: PropTypes.func.isRequired,
  clockOut: PropTypes.func.isRequired,
  adHocClockIn: PropTypes.func.isRequired,
  updateShift: PropTypes.func.isRequired,
  setHideGreeting: PropTypes.func.isRequired
};

function mapStateToProps(state: GlobalState) {
  const {
    clockInEarlyMinutes,
    shiftBreakType,
    user,
    notes,
    automatedBreaksIsEnabled
  } = extractDataFromState(state);

  return {
    clockInEarlyMinutes,
    shiftBreakType,
    user,
    notes,
    isInitialized: state.core.isInitialized,
    isUserLoggedIn: state.authentication.isUserLoggedIn,
    usTimeFormat: state.authentication.usTimeFormat,
    loadingUserShifts: state.timeClock.loadingUserShifts,
    shifts: state.timeClock.shifts,
    shift: state.timeClock.shift,
    adHocShift: state.timeClock.adHocShift,
    personas: user && user.personas,
    appState: state.core.appState,
    screen: state.core.screen,
    screenProps: state.core.screenProps,
    isIntegrationKiosk:
      !!state.authentication.externalUserId && state.authentication.externalUserId.length > 0,
    kioskSessionId: state.core.kioskSessionId,
    hideGreeting: state.timeClock.hideGreeting,
    automatedBreaksIsEnabled
  };
}

type TimeClockStateData = {
  clockInEarlyMinutes: number,
  shiftBreakType: boolean,
  user: User | null,
  notes: Array<Note> | null,
  automatedBreaksIsEnabled: boolean
};

function extractDataFromState(state: GlobalState): TimeClockStateData {
  if (state.timeClock.data) {
    const { user, notes } = state.timeClock.data;
    const {
      clockInEarlyEnabled,
      shiftBreakType,
      clockInEarlyMinutes,
      automatedBreaksIsEnabled
    } = state.timeClock.data.orgSettings;

    return {
      clockInEarlyMinutes: clockInEarlyEnabled ? clockInEarlyMinutes : 0,
      shiftBreakType,
      user,
      notes,
      automatedBreaksIsEnabled
    };
  }

  return {
    clockInEarlyMinutes: 0,
    shiftBreakType: false,
    user: null,
    notes: null,
    automatedBreaksIsEnabled: false
  };
}

export default connect(mapStateToProps, {
  initialize,
  logIn,
  signOut,
  clockIn,
  startBreak,
  startAutomatedBreak,
  endBreak,
  clockOut,
  adHocClockIn,
  updateShift,
  setHideGreeting
})(MainComponent);
